import axios from 'axios';
import { MessageAttachment } from '../types/conversation';

// Define message and thread types here since there's an issue importing from dm.ts
interface DmMessage {
  id: number;
  message_id: string;
  thread_id: string;
  sender_id: string;
  sender_username?: string;
  recipient_id: string;
  recipient_username?: string;
  message_text: string | null;
  message_type?: string;
  timestamp: string;
  is_read: boolean;
  read_timestamp?: string;
  is_echo: boolean;
  reply_to_message_id?: string;
  reply_to_story_id?: string;
  reply_to_story_url?: string;
  reply_to_type?: string;
  attachments?: MessageAttachment[];
  replied_to_message?: {
    id: number;
    message_id: string;
    message_text: string;
  };
  isCustomer?: boolean;
}

interface DmThread {
  thread_id: string;
  brand_id: string;
  brand_name: string;
  platform: string;
  is_active: boolean;
  last_activity: string;
  customer_id: string;
  customer_username?: string;
  created_at?: string;
  updated_at?: string;
  last_message?: DmMessage;
  unread_count?: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Get DM threads for a brand
export const getDmThreads = async (
  brandId: string, 
  page = 1, 
  limit = 10, 
  status?: string,
  search?: string
) => {
  try {
    const response = await axios.get(`${API_URL}/direct-messages/brand/${brandId}`, {
      params: { page, limit, status, search },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching DM threads:', error);
    throw error;
  }
};

// Get messages for a thread
export const getDmMessages = async (threadId: string, page = 1, limit = 20) => {
  try {
    const response = await axios.get(`${API_URL}/direct-messages/thread/${threadId}`, {
      params: { page, limit },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching DM messages:', error);
    throw error;
  }
};

// Send a message in a thread
export const sendDmMessage = async (
  threadId: string,
  text: string,
  recipientId?: string,
  replyToMessageId?: string,
  replyToStoryId?: string,
  replyToType?: string,
  attachments?: MessageAttachment[],
  templateId?: number,
  templateVariables?: Record<string, string>
) => {
  try {
    const response = await axios.post(
      `${API_URL}/direct-messages/thread/${threadId}/message`,
      {
        text,
        recipientId,
        replyToMessageId,
        replyToStoryId,
        replyToType,
        attachments,
        templateId,
        templateVariables
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending DM message:', error);
    throw error;
  }
};

// Update thread status
export const updateThreadStatus = async (
  threadId: string,
  status: 'open' | 'in_progress' | 'done' | 'closed',
  note?: string
) => {
  try {
    const response = await axios.patch(
      `${API_URL}/direct-messages/thread/${threadId}/status`,
      { status, note },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating thread status:', error);
    throw error;
  }
};

// Mark thread as read
export const markThreadAsRead = async (threadId: string) => {
  try {
    const response = await axios.patch(
      `${API_URL}/direct-messages/thread/${threadId}/read`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error marking thread as read:', error);
    throw error;
  }
};

// Get unread counts for brand
export const getUnreadCounts = async (brandId: string) => {
  try {
    const response = await axios.get(
      `${API_URL}/direct-messages/brand/${brandId}/unread-counts`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting unread counts:', error);
    throw error;
  }
}; 